import { useCallback, useMemo } from "react"
import { useLocation, useMatches, useNavigate } from "@remix-run/react"
import type { WithContentfulHeaderLocationFragment } from "~/contentful"
import {
  resolveLocationRouteSlug,
  resolveLocationSlugAlias
} from "~/helpers/location"

type ActivityRouteData = {
  activityType?: {
    slug: string
    availableLocations: Array<{ id: string; name: string; slug: string }>
  }
  locationName?: string
}

/**
 * Location name + navigation when picking a location from the header or hero.
 * Optionally pass `locationSlug` from the layout (e.g. `_page`) so behavior
 * matches the header when route data is available there first.
 */
export function useLocationSelectNavigation(
  locationPages: WithContentfulHeaderLocationFragment[],
  locationSlug?: string | null
) {
  const navigate = useNavigate()
  const currentLocation = useLocation()
  const matches = useMatches()

  const activityData = useMemo(() => {
    const activityMatch = matches.find(
      m => m.id === "routes/_page.$location.activities.$slug"
    )
    return activityMatch?.data as ActivityRouteData | undefined
  }, [matches])

  const locationSlugFromLoader = useMemo(() => {
    const locationData = matches
      .map(
        m =>
          (m.data as { location?: { slug?: string | null } | null } | undefined)
            ?.location
      )
      .find(l => l != null)
    const slug = locationData?.slug
    return slug != null && slug !== "" ? slug : null
  }, [matches])

  const pathnameLocationSlug = currentLocation.pathname
    .split("/")
    .filter(Boolean)[0]
  const pathnameLocationDataSlug =
    resolveLocationSlugAlias(pathnameLocationSlug)

  const cmsLocationSlugFromPathname =
    locationPages.find(lp => lp.slug === pathnameLocationDataSlug)?.slug ?? null

  const cmsLocationSlugFromProp =
    locationSlug != null && locationSlug !== ""
      ? resolveLocationSlugAlias(locationSlug)
      : null

  const cmsLocationSlugFromMatches =
    locationSlugFromLoader != null && locationSlugFromLoader !== ""
      ? resolveLocationSlugAlias(locationSlugFromLoader)
      : null

  const cmsLocationSlug =
    cmsLocationSlugFromProp ??
    cmsLocationSlugFromMatches ??
    cmsLocationSlugFromPathname

  const publicLocationSiteSlug = cmsLocationSlug
    ? resolveLocationRouteSlug(cmsLocationSlug)
    : null

  const effectiveLocationName =
    (cmsLocationSlug != null
      ? locationPages.find(lp => lp.slug === cmsLocationSlug)?.name
      : null) ??
    activityData?.locationName ??
    null

  const getLocationSelectDestination = useCallback(
    (locationName: string) => {
      if (activityData?.activityType) {
        const matchedLocation =
          activityData.activityType.availableLocations.find(
            loc => loc.name === locationName
          )
        if (matchedLocation) {
          const activitySlug = activityData.activityType.slug
          const currentPath = currentLocation.pathname
          const activityBasePattern = /\/activities\/([^/]+)(\/.*)?$/
          const pathMatch = currentPath.match(activityBasePattern)
          const suffix = pathMatch?.[2] ?? ""

          return `/${resolveLocationRouteSlug(matchedLocation.slug)}/activities/${activitySlug}${suffix}`
        }
      }

      const matchedPage = locationPages.find(lp => lp.name === locationName)
      if (matchedPage?.slug) {
        return `/${resolveLocationRouteSlug(matchedPage.slug)}`
      }

      return null
    },
    [activityData, currentLocation.pathname, locationPages]
  )

  const handleSelectLocation = useCallback(
    (locationName: string) => {
      const destination = getLocationSelectDestination(locationName)
      if (destination) {
        navigate(destination)
      }
    },
    [getLocationSelectDestination, navigate]
  )

  return {
    effectiveLocationName,
    handleSelectLocation,
    getLocationSelectDestination,
    cmsLocationSlug,
    publicLocationSiteSlug
  }
}
